/**
 * Suspension Notice Classifier — pure client-side, no AI required.
 *
 * Maps free-text suspension/disable/ban notices to the most likely Meta /
 * TikTok / YouTube / X enforcement category by keyword pattern matching.
 *
 * Categories and language are sourced directly from each platform's published
 * Community Guidelines / Community Standards / Help Center, mirroring the
 * detailed reference articles we publish on the blog.
 *
 * IMPORTANT: This is a *triage* tool, not a definitive diagnosis. The output
 * deliberately uses "likely", "typical", "tends to" language — never
 * percentages or guarantees.
 */

export const CATEGORIES = {
  csam: {
    id: "csam",
    label: "Child Safety Policy (CSAM)",
    severity: "critical",
    summary:
      "The platform cites a child-safety violation. This is the highest-severity enforcement category across every platform and effectively never reversed for confirmed content. Misclassification appeals require government-issued ID and extensive context.",
    appealOutlook: "Appeals rarely succeed unless the case is a clear misclassification of non-sexual content of one's own child. Submission requires identity verification.",
    recommendedTier: "free_reference",
  },
  dangerous_orgs: {
    id: "dangerous_orgs",
    label: "Dangerous Organizations & Individuals (DOI)",
    severity: "high",
    summary:
      "The platform's automated systems have associated your content with a designated dangerous organization (terrorist, hate, criminal cartel) or individual. Often triggered by sharing imagery for journalistic, condemnatory, or educational purposes.",
    appealOutlook: "Better outlook than CSAM appeals when content has clear journalistic, condemnatory, or educational purpose. Documentation of intent is critical.",
    recommendedTier: "appeal_package",
  },
  hate_speech: {
    id: "hate_speech",
    label: "Hate Speech",
    severity: "high",
    summary:
      "Attacks based on protected characteristics (race, ethnicity, national origin, disability, religion, caste, sexual orientation, gender, serious disease). Counter-speech, education, and condemnation of hate are protected but frequently misclassified by automated systems.",
    appealOutlook: "Context matters significantly. Misclassified counter-speech and educational appeals have moderate-to-good outlook.",
    recommendedTier: "appeal_package",
  },
  violence_incitement: {
    id: "violence_incitement",
    label: "Violence & Incitement",
    severity: "high",
    summary:
      "Credible threats of violence, statements of intent to harm, or 'calls to arms.' Sarcasm and hyperbole are protected but require careful context.",
    appealOutlook: "Sarcasm/hyperbole appeals can succeed but need careful documentation of intent.",
    recommendedTier: "appeal_package",
  },
  bullying_harassment: {
    id: "bullying_harassment",
    label: "Bullying & Harassment",
    severity: "medium",
    summary:
      "Targeted abuse of a private individual, or severe attacks against public figures (doxxing, sexualized attacks, severe threats).",
    appealOutlook: "Comedy/parody appeals have moderate success; targeted-harassment patterns are harder to overturn.",
    recommendedTier: "appeal_package",
  },
  sexual_solicitation: {
    id: "sexual_solicitation",
    label: "Adult Sexual Solicitation",
    severity: "high",
    summary:
      "Solicitation of paid or unpaid sexual services, explicit DM language to non-consenting parties, or overt offers. Adult-content creators flagged here frequently lose accounts permanently.",
    appealOutlook: "Appeals rarely succeed. Some platforms treat this as one of their highest-severity categories.",
    recommendedTier: "free_reference",
  },
  nudity_sexual: {
    id: "nudity_sexual",
    label: "Adult Nudity & Sexual Activity",
    severity: "medium",
    summary:
      "Nudity or sexual content, with documented exceptions for breastfeeding, post-mastectomy, protest, art, and medical contexts. Automated systems frequently misclassify protected exceptions.",
    appealOutlook: "Better outlook when content falls into a documented exception and was misclassified by automation.",
    recommendedTier: "case_review",
  },
  fraud_deception: {
    id: "fraud_deception",
    label: "Fraud & Deception",
    severity: "medium-high",
    summary:
      "Misrepresentation of identity, payment fraud, fake reviews, ticket-scalping schemes, multi-level-marketing manipulation, or impersonation. Often triggered by identity overlap with previously-disabled accounts or by customer reports.",
    appealOutlook: "Strong appeals require government-issued ID, evidence of legitimate business model, and explanation of any chargebacks or disputes.",
    recommendedTier: "appeal_package",
  },
  inauthentic_behavior: {
    id: "inauthentic_behavior",
    label: "Coordinated Inauthentic Behavior (CIB)",
    severity: "high",
    summary:
      "Networks of fake accounts working together, cross-account ad delivery coordination, sockpuppet engagement manipulation, or stolen-identity ad accounts. One of the lowest appeal-success categories.",
    appealOutlook: "Appeals rarely succeed when the platform has multi-account fingerprinting evidence. Best path is often a legal documentation channel.",
    recommendedTier: "priority_support",
  },
  privacy_violations: {
    id: "privacy_violations",
    label: "Privacy Violations / NCII",
    severity: "medium-high",
    summary:
      "Sharing personally identifiable information without consent, doxxing, or non-consensual intimate imagery. Most privacy removals follow a user report rather than automated detection.",
    appealOutlook: "Hard to reverse because the platform usually defers to the reporting party for borderline cases.",
    recommendedTier: "case_review",
  },
  restricted_goods: {
    id: "restricted_goods",
    label: "Restricted Goods & Services",
    severity: "medium",
    summary:
      "Sale of firearms, marijuana, pharmaceuticals, live animals, alcohol, body parts, or hazardous goods. Some categories have regional carve-outs (e.g., cannabis in legal-state US).",
    appealOutlook: "Can succeed if the product is legal in your jurisdiction and the policy has a documented regional exception.",
    recommendedTier: "appeal_package",
  },
  graphic_violence: {
    id: "graphic_violence",
    label: "Violent & Graphic Content",
    severity: "medium",
    summary:
      "Glorification of violence, graphic depictions of injury or death, animal abuse. News and educational context is protected but heavily age-gated.",
    appealOutlook: "News/journalism appeals have moderate success when the source can be documented.",
    recommendedTier: "case_review",
  },
  self_harm: {
    id: "self_harm",
    label: "Suicide, Self-Injury & Eating Disorders",
    severity: "medium",
    summary:
      "Promotion or encouragement of self-harm. Recovery content, discussion of personal experience, and educational content are documented exceptions.",
    appealOutlook: "Recovery-context appeals usually succeed when supportive intent is clearly documented.",
    recommendedTier: "case_review",
  },
  hacked_compromised: {
    id: "hacked_compromised",
    label: "Hacked / Compromised Account",
    severity: "medium",
    summary:
      "Your account was taken over by an unauthorized third party. The platform may have disabled it preemptively or in response to suspicious activity. This is a recovery path, not an appeal path — the workflow is different.",
    appealOutlook: "Recovery rate depends heavily on (a) whether you can prove identity, (b) whether the hacker changed the email/phone, and (c) how quickly you act.",
    recommendedTier: "appeal_package",
  },
  business_quality: {
    id: "business_quality",
    label: "Business Account Quality / Advertising Standards",
    severity: "medium",
    summary:
      "For Business Manager / ad-account disables: violations of Advertising Standards (personal-attribute targeting, misleading claims, restricted categories), or quality issues (low feedback scores, high disapproval rates).",
    appealOutlook: "Strong appeals cite the specific Advertising Standard section, document business legitimacy (LLC, EIN, domain ownership), and proactively address any prior violations.",
    recommendedTier: "appeal_package",
  },
  spam_generic: {
    id: "spam_generic",
    label: "Spam / Generic Community Standards",
    severity: "low-medium",
    summary:
      "Generic 'spam' or 'community standards violation' flag without a specific cited policy. Often the result of bulk reporting, hashtag overuse, or rapid follow/unfollow patterns flagged by automation.",
    appealOutlook: "Often reversible with a clear written appeal that addresses likely automated triggers (engagement patterns, hashtag usage).",
    recommendedTier: "case_review",
  },
  unknown: {
    id: "unknown",
    label: "Uncategorized Enforcement",
    severity: "unknown",
    summary:
      "We couldn't pattern-match the notice to a specific enforcement category. This is common when notices use generic language like 'violated our terms.' A human review of the full notice + account context is the strongest path.",
    appealOutlook: "Without a clear category, the strongest move is a manual review of your specific notice + account history.",
    recommendedTier: "case_review",
  },
};

// Order matters — first match wins, so more-specific categories appear first.
const PATTERNS = [
  { cat: "csam", patterns: ["child sexual", "child exploit", "csam", "minor sexual", "child safety"] },
  { cat: "dangerous_orgs", patterns: ["dangerous organization", "dangerous individual", "terrorist", "hate organization", "designated entity", "doi"] },
  { cat: "inauthentic_behavior", patterns: ["coordinated inauthentic", "cib", "fake account network", "platform manipulation", "sockpuppet"] },
  { cat: "hacked_compromised", patterns: ["hacked", "compromised", "unauthorized access", "someone else accessed", "taken over", "unauthorized login"] },
  { cat: "sexual_solicitation", patterns: ["sexual solicitation", "solicit sexual", "adult solicitation", "selling sexual", "escort", "onlyfans link"] },
  { cat: "nudity_sexual", patterns: ["nudity", "sexual activity", "adult content", "sexual content", "sexually explicit", "explicit content", "adult"] },
  { cat: "hate_speech", patterns: ["hate speech", "hateful conduct", "hateful behavior", "slur", "discrimination based on", "protected characteristic"] },
  { cat: "violence_incitement", patterns: ["violence and incitement", "credible threat", "threat of violence", "calls to violence", "incitement", "violent threat"] },
  { cat: "bullying_harassment", patterns: ["bullying", "harassment", "targeted harassment", "abuse of an individual", "abusive behavior", "doxx"] },
  { cat: "privacy_violations", patterns: ["privacy violation", "personal information", "non-consensual", "intimate image", "ncii", "doxxing", "private information"] },
  { cat: "restricted_goods", patterns: ["restricted goods", "regulated goods", "firearm", "pharmaceut", "controlled substance", "drug", "weapons"] },
  { cat: "graphic_violence", patterns: ["graphic violence", "graphic content", "violent content", "injury", "blood", "gore"] },
  { cat: "self_harm", patterns: ["self-harm", "self injury", "suicide", "eating disorder", "thinspo"] },
  { cat: "fraud_deception", patterns: ["fraud", "deception", "misleading", "scam", "impersonation", "fake review", "deceptive practice"] },
  { cat: "business_quality", patterns: ["advertising standards", "ads policy", "business standards", "account quality", "ad account", "billing", "feedback score", "page quality", "business manager"] },
  { cat: "spam_generic", patterns: ["spam", "automated behavior", "bot-like", "community guidelines", "community standards", "terms of service", "terms of use", "platform policies", "policy violation"] },
];

/**
 * Returns the most likely category given a free-text notice + structured
 * answers. Falls back to "unknown" only if no signal at all is found.
 */
export function classifySuspension({ noticeText = "", suspensionType = "", accountType = "" } = {}) {
  const text = `${noticeText} ${suspensionType} ${accountType}`.toLowerCase();

  // Hacked is signaled strongly by suspensionType alone
  if (suspensionType === "hacked") return CATEGORIES.hacked_compromised;

  // Business / ad account signals
  if (accountType === "ad_account" || accountType === "business_manager") {
    if (PATTERNS.find((p) => p.cat === "business_quality" && p.patterns.some((kw) => text.includes(kw)))) {
      return CATEGORIES.business_quality;
    }
    // Even without specific keyword, default business cases to business_quality
    if (!noticeText.trim()) return CATEGORIES.business_quality;
  }

  for (const { cat, patterns } of PATTERNS) {
    if (patterns.some((kw) => text.includes(kw))) {
      return CATEGORIES[cat];
    }
  }

  return CATEGORIES.unknown;
}

/**
 * Map a recommended tier ID into a concrete CTA + price label.
 */
export const TIER_INFO = {
  free_reference: {
    label: "Read the free reference article",
    price: "Free",
    cta: "Read our reference article",
    href: "/blog",
    rationale:
      "This category has a documented low appeal-success rate. Before spending money on documentation, we recommend reading the free reference article so you can make an informed decision about whether a paid appeal is worth pursuing.",
  },
  case_review: {
    label: "Case Review",
    price: "$99",
    cta: "Start a $99 Case Review",
    href: "/contact",
    rationale:
      "A Case Review is a single structured appeal document you can submit yourself. Best for cases where you understand the cited policy but need help with how to write the appeal.",
  },
  appeal_package: {
    label: "Appeal Package",
    price: "$399",
    cta: "Start a $399 Appeal Package",
    href: "/contact",
    rationale:
      "We prepare the full appeal package, organize your evidence, and submit through official channels with your authorization. This is the strongest path for most personal and small-business cases.",
  },
  priority_support: {
    label: "Priority Support",
    price: "$499",
    cta: "Start a $499 Priority Support case",
    href: "/contact",
    rationale:
      "For time-critical business cases or cases involving Business Manager / ad-account cascades. Includes 24-hour turnaround on documentation and follow-up support through the review window.",
  },
};

/**
 * Recommend the appropriate evidence checklist based on category + account type.
 */
export function buildEvidenceChecklist({ category, accountType, platform }) {
  const base = [
    "Screenshot of the suspension/disable notification (full email with timestamp)",
    "Screenshot of the account login screen showing the restriction",
    "Government-issued photo ID matching the account name (passport, driver's license)",
    "A short written timeline of recent account activity (last 30 days before the action)",
  ];

  const businessAddons = [
    "LLC / corporation registration document (Articles of Organization or equivalent)",
    "EIN letter from the IRS",
    "Business website URL matching the domain you advertise from",
    "Most recent ad account billing statement (for ad-account cases)",
  ];

  const hackedAddons = [
    "Original email address used to register the account (with proof of access if changed)",
    "Phone number originally linked to the account",
    "Approximate date the account was created",
    "List of approximate followers / posts / username (for the platform to verify ownership)",
  ];

  const fraudAddons = [
    "Documentation of your legitimate business model (terms of service, refund policy)",
    "Resolution of any prior customer complaints or chargebacks",
  ];

  const checklist = [...base];
  if (accountType === "business" || accountType === "ad_account" || accountType === "business_manager") {
    checklist.push(...businessAddons);
  }
  if (category.id === "hacked_compromised") {
    checklist.push(...hackedAddons);
  }
  if (category.id === "fraud_deception" || category.id === "inauthentic_behavior") {
    checklist.push(...fraudAddons);
  }

  // Platform-specific
  if (platform === "facebook" || platform === "instagram") {
    checklist.push("Any case/reference number from previous Meta correspondence");
  }
  if (platform === "youtube") {
    checklist.push("YouTube channel ID (found in YouTube Studio → Settings → Channel)");
  }
  if (platform === "tiktok") {
    checklist.push("TikTok username (without @) and the device originally used to register");
  }

  return checklist;
}
